import React, { useState, useEffect } from "react";
import axios from "axios";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput"; // 👈 Import MDInput for the search bar

import { setSelectedLocation } from "features/geoFence/geoFenceSlice";
import { useSelector, useDispatch } from "react-redux";
import { setLocationRange } from "features/geoFence/geoFenceSlice";
import {
  clearLocationRange,
  setPolygonGeofence,
} from "features/geoFence/geoFenceSlice";
import { getGeofence, removeGeofence } from "config/apis";
import { useMaterialUIController } from "context";

import {
  clearSelectedLocation,
  setAllLocationRanges,
  setRefresh
} from "features/geoFence/geoFenceSlice";

const GeoFenceCard = () => {
  const [geoFences, setGeoFences] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // 👈 State for search input
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fenceToRemove, setFenceToRemove] = useState(null);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const token = localStorage.getItem("token");
  
  const refresh = useSelector((state) => state.geoFence.refresh);
  const dispatch = useDispatch();

  const fetchGeoFences = async () => {
    try {
      const response = await axios.post(
        getGeofence,
        {},
        { headers: { "x-dhundhoo-session": token } }
      );

      const formattedGeoFences = response.data.map((fence) => {
        const center = fence.data?.coordinates
          ? (() => {
              const coords = fence.data.coordinates;
              const n = coords.length;
              const sum = coords.reduce(
                (acc, [lng, lat]) => {
                  acc.lng += lng;
                  acc.lat += lat;
                  return acc;
                },
                { lng: 0, lat: 0 }
              );
              return { lng: sum.lng / n, lat: sum.lat / n };
            })()
          : { lng: 0, lat: 0 };

        return {
          name: fence.tags[0] || "Unnamed Fence",
          radius: fence.data.radius || 0,
          filterId: fence.filterId,
          latitude: fence.data?.location?.latitude || center.lat || 0,
          longitude: fence.data?.location?.longitude || center.lng || 0,
          adminOnly: fence.data?.adminOnly || false,
          coordinates: fence.data?.coordinates || null,
          type: fence.type,
        };
      });

      setGeoFences(formattedGeoFences);
      dispatch(setAllLocationRanges(response.data));
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGeoFences();
  }, [token, refresh]);

  const handleEdit = (fence) => {
    dispatch(clearLocationRange());
    
    dispatch(
      setSelectedLocation({
        lat: fence.latitude,
        lng: fence.longitude,
        filterId: fence.filterId,
        radius: fence.radius,
        tags: fence.name,
        adminOnly: fence.adminOnly,
        coordinates: fence.coordinates,
        type: fence.type,
      })
    );
    dispatch(
      setPolygonGeofence({
        lat: fence.latitude,
        lng: fence.longitude,
        filterId: fence.filterId,
        radius: fence.radius,
        tags: fence.name,
        adminOnly: fence.adminOnly,
      })
    );
  };

  const handleRemoveClick = (fence) => {
    setFenceToRemove(fence);
    dispatch(
      setLocationRange({
        lat: fence.latitude,
        lng: fence.longitude,
        filterId: fence.filterId,
        radius: fence.radius,
      })
    );
    setShowConfirm(true);
  };

  const handleLocation = (fence) => {
    dispatch(clearSelectedLocation());
    dispatch(
      setLocationRange({
        lat: fence.latitude,
        lng: fence.longitude,
        filterId: fence.filterId,
        radius: fence.radius,
      })
    );
  };
  
  const confirmRemove = async () => {
    if (!fenceToRemove) return;

    try {
      await axios.post(
        `${removeGeofence}/${fenceToRemove.filterId}`,
        {},
        { headers: { "x-dhundhoo-session": token } }
      );

      setGeoFences((prev) =>
        prev.filter((f) => f.filterId !== fenceToRemove.filterId)
      );

      dispatch(clearLocationRange());
      dispatch(clearSelectedLocation());
      
      dispatch(setRefresh(!refresh));

    } catch (err) {
      console.error("Failed to remove geofence:", err);
      alert("Failed to remove geofence");
    } finally {
      setShowConfirm(false);
      setFenceToRemove(null);
    }
  };

  // 👈 Filter logic for the geofences
  const filteredGeoFences = geoFences.filter((fence) =>
    fence.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <MDBox sx={{ p: 2 }}><MDTypography>Loading...</MDTypography></MDBox>;
  if (error) return <MDBox sx={{ p: 2 }}><MDTypography color="error">Error: {error}</MDTypography></MDBox>;

  return (
    <MDBox
      sx={{
        flexShrink: 0,
        height: "100%",
        overflowY: "auto",
        background: darkMode ? "#1f283e" : "#f0f2f5",
        p: 2,
      }}
      onMouseLeave={() => {
        dispatch(clearLocationRange());
        dispatch(clearSelectedLocation());
      }}
    >
      {/* 👈 Added the Search Input Field */}
      <MDBox mb={2} sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: darkMode ? "#1f283e" : "#f0f2f5", pt: 1, pb: 1 }}>
        <MDInput
          fullWidth
          placeholder="Search saved geofences..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ backgroundColor: darkMode ? "#1a2035" : "white", borderRadius: "8px" }}
        />
      </MDBox>

      {filteredGeoFences.length === 0 ? (
        <MDTypography>{searchTerm ? "No matching geofence found" : "No geo-fences available"}</MDTypography>
      ) : (
        filteredGeoFences.map((fence, index) => (
          <MDBox
            key={index}
            sx={{
              backgroundColor: "white",
              p: 2,
              mb: 2,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              cursor: "pointer",
            }}
          >
            <MDBox
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}
              onMouseEnter={() => handleLocation(fence)}
            >
              <MDTypography variant="button" fontSize="15px">{fence.name}</MDTypography>
              <MDTypography variant="caption" color="text">
                {fence.type === "POLYGON" ? "Polygon" : `${fence.radius}m`}
              </MDTypography>
            </MDBox>

            <MDBox sx={{ display: "flex", gap: 1, mt: 1 }}>
              <MDButton variant="outlined" color="success" fullWidth onClick={() => handleEdit(fence)}>
                Edit
              </MDButton>
              <MDButton variant="outlined" color="error" fullWidth onClick={() => handleRemoveClick(fence)}>
                Remove
              </MDButton>
            </MDBox>
          </MDBox>
        ))
      )}

      {showConfirm && (
        <MDBox
          style={{
            position: "fixed", top: "0", left: "0", width: "100%", height: "100%",
            backgroundColor: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "center", alignItems: "center",
            zIndex: 9999,
          }}
        >
          <MDBox style={{ background: "white", padding: "20px", borderRadius: "8px", textAlign: "center", minWidth: "300px" }}>
            <MDTypography variant="h6" mb={2}>Do you want to remove this geofence?</MDTypography>
            <MDBox display="flex" justifyContent="center" gap={2} mt={2}>
              <MDButton variant="outlined" color="secondary" onClick={() => setShowConfirm(false)}>Cancel</MDButton>
              <MDButton variant="contained" color="info" onClick={confirmRemove}>OK</MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      )}
    </MDBox>
  );
};

export default GeoFenceCard;